/**
 * Apostille / MFA-legalisation Q&A — Wave 3.
 *
 * ACCURACY POLICY (ตรวจทาน ส.ค. 2026)
 * - ไทยยื่นภาคยานุวัติอนุสัญญา Apostille 30 มิ.ย. 2026 · อนุสัญญามีผลกับไทย 28 ก.พ. 2027
 *   → ปัจจุบันกรมการกงสุล "ยังไม่ออกตรา Apostille" (hcch.net · mfa.go.th · src/data/legal-facts.ts)
 * - เส้นทางปัจจุบัน: ผู้ออกเอกสาร/ทนาย Notary → นิติกรณ์กรมการกงสุล → สถานทูตปลายทาง (ถ้ากำหนด)
 * - ค่าธรรมเนียมนิติกรณ์ 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ห้ามระบุค่าบริการของสำนักงาน · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import type { ApostilleQA } from "./questions-500";
import { APOSTILLE_STATUS } from "@/data/legal-facts";

const NOTE =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";
const CTA = "สอบถามรายละเอียดและค่าบริการทางโทรศัพท์ LINE หรืออีเมล";
const STATUS = `ไทยยังไม่ออกตรา Apostille — อนุสัญญาจะมีผลกับไทยวันที่ ${APOSTILLE_STATUS.inForceDateTh} ระหว่างนี้ใช้เส้นทางนิติกรณ์กรมการกงสุลแล้วรับรองสถานทูตปลายทางเมื่อปลายทางกำหนด`;
const FEE =
  "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน (consular.mfa.go.th) ค่าธรรมเนียมราชการแยกจากค่าบริการของสำนักงานเสมอ";

export const APOSTILLE_QA_WAVE3: ApostilleQA[] = [
  // ── สถานะกฎหมาย / ช่วงเปลี่ยนผ่าน ────────────────────────────────
  {
    slug: "apostille-ไทยเริ่มใช้เมื่อไหร่-นับอย่างไร",
    q: "ไทยเริ่มออก Apostille เมื่อไหร่ และนับวันอย่างไร?",
    aShort: `${STATUS} เอกสารที่ต้องใช้ก่อนวันดังกล่าวจึงยังต้องเดินเส้นทางนิติกรณ์ตามปกติ`,
    aLong: `วันที่อนุสัญญามีผลกับรัฐภาคีใหม่นับตามกลไกของอนุสัญญา ไม่ใช่วันที่ยื่นภาคยานุวัติ ดังนั้นเอกสารที่ยื่นก่อนวันมีผลจะยังใช้ตรานิติกรณ์ของกรมการกงสุลและตราสถานทูตปลายทางตามเดิม ผู้ที่มีกำหนดยื่นในช่วงคาบเกี่ยวควรถามหน่วยงานปลายทางว่ารับรูปแบบใดในวันที่ยื่นจริง ${FEE} ${NOTE} ${CTA}`,
    synonyms: [
      "apostille ไทย เริ่มเมื่อไหร่",
      "thailand apostille start date",
      "อนุสัญญา apostille มีผลกับไทยวันไหน",
      "apostille convention thailand in force",
    ],
    cluster: "definition",
  },
  {
    slug: "apostille-ช่วงเปลี่ยนผ่าน-ทำอย่างไรดี",
    q: "ช่วงเปลี่ยนผ่านก่อนไทยออก Apostille ควรวางแผนอย่างไร?",
    aShort:
      "วางแผนจากวันที่หน่วยงานปลายทางรับเอกสารจริง ไม่ใช่วันที่เราส่งเอกสาร ถ้ากำหนดยื่นอยู่ก่อนวันอนุสัญญามีผล ให้เดินเส้นทางนิติกรณ์เดิมทั้งชุด",
    aLong: `กรณีที่กำหนดยื่นอยู่ใกล้วันอนุสัญญามีผล ทางที่ปลอดภัยคือทำเส้นทางเดิมให้จบ เพราะเอกสารที่ผ่านนิติกรณ์และตราสถานทูตแล้วยังใช้ได้ตามอายุที่ปลายทางกำหนด ส่วนเอกสารที่ยังไม่เร่ง อาจรอรูปแบบใหม่เพื่อลดขั้นตอน แต่ต้องยืนยันกับปลายทางก่อนเสมอว่าจะรับตรา Apostille ของไทยทันทีหรือไม่ ${NOTE} ${CTA}`,
    synonyms: [
      "ช่วงเปลี่ยนผ่าน apostille ไทย",
      "รอ apostille หรือทำ mfa ก่อน",
      "apostille transition period thailand",
    ],
    cluster: "process",
  },
  {
    slug: "apostille-ประเทศปลายทางไม่ใช่ภาคี-ทำอย่างไร",
    q: "ถ้าประเทศปลายทางไม่ได้เป็นภาคีอนุสัญญา ต้องทำอย่างไร?",
    aShort:
      "ต้องใช้เส้นทางรับรองเต็มรูปแบบเสมอ คือ นิติกรณ์กรมการกงสุล แล้วนำไปประทับตรารับรองที่สถานทูตหรือสถานกงสุลของประเทศนั้นในไทย",
    aLong: `อนุสัญญา Apostille ใช้ได้เฉพาะระหว่างรัฐภาคีด้วยกัน ประเทศที่ไม่ได้เป็นภาคี เช่น หลายประเทศในตะวันออกกลางและบางประเทศในเอเชีย ยังต้องใช้ระบบ legalisation แบบสองชั้น เอกสารต้องผ่านกรมการกงสุลก่อน สถานทูตปลายทางจึงจะรับประทับตราให้ ${FEE} ${NOTE} ${CTA}`,
    synonyms: [
      "ประเทศไม่ใช่ภาคี apostille",
      "non hague country legalisation",
      "รับรองเอกสารประเทศนอกภาคี",
      "consular legalisation thailand",
    ],
    cluster: "country",
  },

  // ── ขั้นตอน / เอกสาร ──────────────────────────────────────────────
  {
    slug: "apostille-ยื่นด้วยตัวเองกับมอบอำนาจต่างกันไหม",
    q: "ยื่นนิติกรณ์ด้วยตัวเองกับมอบอำนาจให้ผู้อื่นยื่น ต่างกันไหม?",
    aShort:
      "ผลของตราประทับเหมือนกัน ต่างกันที่เอกสารประกอบ การมอบอำนาจต้องมีหนังสือมอบอำนาจพร้อมสำเนาบัตรประชาชนของผู้มอบและผู้รับมอบ",
    aLong: `กรมการกงสุลรับคำร้องทั้งกรณีเจ้าของเอกสารมาเอง และกรณีผู้รับมอบอำนาจ สิ่งที่ทำให้ถูกตีกลับบ่อยคือ หนังสือมอบอำนาจไม่ระบุขอบเขตงานให้ชัด ลายมือชื่อไม่ตรงกับบัตร หรือสำเนาบัตรไม่ได้ลงนามรับรองสำเนาถูกต้อง ควรตรวจสามจุดนี้ก่อนเดินทาง ${NOTE} ${CTA}`,
    synonyms: [
      "มอบอำนาจยื่นนิติกรณ์",
      "power of attorney mfa legalisation thailand",
      "ให้คนอื่นไปยื่นกรมการกงสุลได้ไหม",
    ],
    cluster: "process",
  },
  {
    slug: "apostille-สำเนาที่รับรองแล้วใช้แทนต้นฉบับได้ไหม",
    q: "สำเนาที่รับรองแล้วใช้แทนต้นฉบับในการทำนิติกรณ์ได้ไหม?",
    aShort:
      "ขึ้นกับประเภทเอกสารและปลายทาง เอกสารทะเบียนราษฎรส่วนใหญ่ปลายทางขอฉบับที่ออกโดยหน่วยงานผู้ออกโดยตรง ไม่ใช่สำเนาถ่ายเอกสาร",
    aLong: `หลักคือ ยิ่งเอกสารมีผลผูกพันทางกฎหมายสูง ปลายทางยิ่งขอฉบับจากผู้ออก เช่น สูติบัตร ทะเบียนสมรส ทะเบียนบ้าน ส่วนเอกสารบางประเภท เช่น วุฒิการศึกษาหรือเอกสารบริษัท อาจใช้สำเนาที่ผู้ออกรับรองได้ ทางที่ปลอดภัยคือขอฉบับใหม่จากผู้ออกเมื่อทำได้ เพราะการยื่นซ้ำเสียเวลามากกว่าการขอเอกสารใหม่ตั้งแต่แรก ${NOTE} ${CTA}`,
    synonyms: [
      "สำเนารับรองใช้ทำนิติกรณ์ได้ไหม",
      "certified copy legalisation thailand",
      "ต้องใช้ต้นฉบับไหม กรมการกงสุล",
    ],
    cluster: "documents",
  },
  {
    slug: "apostille-เอกสารหลายฉบับยื่นพร้อมกันได้ไหม",
    q: "เอกสารหลายฉบับยื่นพร้อมกันได้ไหม และคิดค่าธรรมเนียมอย่างไร?",
    aShort:
      "ยื่นพร้อมกันได้ในคำร้องเดียว แต่ค่าธรรมเนียมนิติกรณ์คิดต่อตราประทับ ไม่ใช่ต่อคำร้อง จำนวนตราจึงขึ้นกับจำนวนฉบับและจำนวนชั้นที่ต้องรับรอง",
    aLong: `${FEE} เอกสารหนึ่งชุดอาจต้องใช้มากกว่าหนึ่งตรา เช่น ตราสำหรับต้นฉบับและอีกตราสำหรับคำแปล ผู้ยื่นควรนับจำนวนฉบับที่ปลายทางขอจริงก่อน เพื่อไม่ให้จ่ายค่าธรรมเนียมเกินความจำเป็นหรือได้ไม่ครบแล้วต้องกลับมายื่นใหม่ ${NOTE} ${CTA}`,
    synonyms: [
      "ค่าธรรมเนียมนิติกรณ์ต่อฉบับ",
      "mfa legalisation fee per stamp",
      "ยื่นหลายฉบับพร้อมกัน กรมการกงสุล",
    ],
    cluster: "process",
  },
  {
    slug: "apostille-คำแปลต้องแปลโดยใคร",
    q: "คำแปลที่ยื่นนิติกรณ์ต้องแปลโดยใคร?",
    aShort:
      "กรมการกงสุลตรวจความถูกต้องของคำแปลเทียบต้นฉบับ ผู้แปลจึงต้องแปลครบทุกข้อความรวมถึงตราประทับและลายมือชื่อ และสะกดชื่อให้ตรงกับหนังสือเดินทาง",
    aLong: `สาเหตุที่คำแปลถูกตีกลับบ่อยที่สุดคือ แปลตกข้อความในตราประทับ ใช้ชื่อสะกดคนละแบบกับหนังสือเดินทาง แปลเลขที่เอกสารผิด และไม่ได้แปลข้อความท้ายเอกสาร ควรตรวจทานสี่จุดนี้ก่อนยื่น หากปลายทางระบุว่าต้องเป็นนักแปลในบัญชีของสถานทูต ต้องใช้นักแปลตามบัญชีนั้นโดยเฉพาะ ${NOTE} ${CTA}`,
    synonyms: [
      "ใครแปลเอกสารยื่นกรมการกงสุลได้",
      "translation for mfa legalisation thailand",
      "คำแปลถูกตีกลับ เพราะอะไร",
    ],
    cluster: "documents",
  },

  // ── ปัญหา / สาเหตุถูกตีกลับ ───────────────────────────────────────
  {
    slug: "apostille-สาเหตุที่ถูกตีกลับบ่อยที่สุด",
    q: "สาเหตุที่เอกสารถูกตีกลับจากนิติกรณ์บ่อยที่สุดคืออะไร?",
    aShort:
      "ห้าสาเหตุหลักคือ ต้นฉบับไม่ใช่ฉบับจากผู้ออก คำแปลไม่ครบถ้วน ชื่อสะกดไม่ตรงหนังสือเดินทาง เอกสารเกินอายุที่ปลายทางกำหนด และลายมือชื่อผู้ลงนามไม่อยู่ในฐานข้อมูลตัวอย่างลายมือชื่อ",
    aLong: `การตีกลับส่วนใหญ่แก้ได้ตั้งแต่ต้นทาง ตรวจว่าเอกสารออกโดยหน่วยงานที่ปลายทางยอมรับ ตรวจอายุเอกสารเทียบกับเงื่อนไขของปลายทาง ตรวจการสะกดชื่อทุกจุดให้ตรงกัน และให้แน่ใจว่าคำแปลครอบคลุมข้อความทุกส่วน กรณีลายมือชื่อผู้ลงนามไม่อยู่ในฐานข้อมูล ต้องกลับไปให้หน่วยงานผู้ออกรับรองก่อน ${NOTE} ${CTA}`,
    synonyms: [
      "เอกสารถูกตีกลับ กรมการกงสุล",
      "why mfa legalisation rejected",
      "นิติกรณ์ไม่ผ่าน ทำไง",
      "ข้อควรระวังยื่นนิติกรณ์",
    ],
    cluster: "problem",
  },
  {
    slug: "apostille-ชื่อสะกดไม่ตรงพาสปอร์ต-แก้อย่างไร",
    q: "ชื่อในเอกสารสะกดไม่ตรงกับหนังสือเดินทาง ต้องแก้อย่างไร?",
    aShort:
      "แก้ที่ต้นทางเสมอ ถ้าเป็นความคลาดเคลื่อนของการถอดอักษรในคำแปล ให้แก้คำแปลให้ตรงหนังสือเดินทาง แต่ถ้าต้นฉบับผิด ต้องให้หน่วยงานผู้ออกแก้ไขก่อน",
    aLong: `ปลายทางส่วนใหญ่ยึดการสะกดตามหนังสือเดินทางเป็นหลัก เมื่อการสะกดในต้นฉบับกับหนังสือเดินทางต่างกันด้วยเหตุผลทางประวัติ เช่น เคยเปลี่ยนชื่อสกุล ควรเตรียมเอกสารเชื่อมโยง เช่น หนังสือสำคัญการเปลี่ยนชื่อ พร้อมคำแปล เพื่อให้ปลายทางตรวจสอบความเป็นบุคคลเดียวกันได้ ${NOTE} ${CTA}`,
    synonyms: [
      "ชื่อไม่ตรงพาสปอร์ต เอกสารราชการ",
      "name mismatch passport document thailand",
      "เปลี่ยนชื่อ เอกสารเก่า ใช้ได้ไหม",
    ],
    cluster: "problem",
  },
  {
    slug: "apostille-เอกสารมีอายุกี่เดือน",
    q: "เอกสารที่ผ่านนิติกรณ์แล้วมีอายุใช้งานนานเท่าไหร่?",
    aShort:
      "ตัวตราประทับไม่ได้กำหนดวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางมักกำหนดอายุของเอกสารต้นทาง เช่น ออกไม่เกิน 3 หรือ 6 เดือนก่อนวันยื่น",
    aLong: `เวลาที่ต้องระวังคืออายุนับจากวันที่ผู้ออกออกเอกสาร ไม่ใช่วันที่ประทับตรา ผู้ยื่นที่ทำเอกสารล่วงหน้านานเกินไปจึงอาจต้องขอเอกสารใหม่ทั้งชุด แนะนำให้เริ่มกระบวนการเมื่อทราบกำหนดยื่นแล้ว และเผื่อเวลาสำหรับคิวของแต่ละหน่วยงาน ${NOTE} ${CTA}`,
    synonyms: [
      "เอกสารรับรองมีอายุกี่เดือน",
      "validity of legalised document thailand",
      "อายุเอกสาร 6 เดือน สถานทูต",
    ],
    cluster: "process",
  },

  // ── เปรียบเทียบ / inbound ────────────────────────────────────────
  {
    slug: "apostille-vs-notary-vs-mfa-ต่างกันตรงไหน",
    q: "Notary กับนิติกรณ์กรมการกงสุล และ Apostille ต่างกันตรงไหน?",
    aShort:
      "ทนาย Notary รับรองลายมือชื่อหรือสำเนาในระดับเอกชน กรมการกงสุลรับรองความถูกต้องของเอกสารราชการหรือคำแปล ส่วน Apostille คือรูปแบบตรามาตรฐานที่ใช้ระหว่างรัฐภาคี",
    aLong: `ทั้งสามอย่างอยู่คนละชั้นของห่วงโซ่การรับรอง เอกสารเอกชนมักเริ่มที่ทนาย Notary ก่อน แล้วจึงไปนิติกรณ์ ส่วนเอกสารราชการเข้ากรมการกงสุลได้โดยตรง ${STATUS} เมื่อถึงเวลาที่ไทยออกตรา Apostille ชั้นสถานทูตปลายทางจะลดลงสำหรับรัฐภาคีเท่านั้น ${NOTE} ${CTA}`,
    synonyms: [
      "notary vs mfa vs apostille",
      "ต่างกันอย่างไร รับรองเอกสาร 3 แบบ",
      "ขั้นตอนรับรองเอกสารมีกี่ชั้น",
    ],
    cluster: "compare",
  },
  {
    slug: "apostille-เอกสารต่างประเทศเข้ามาใช้ในไทย",
    q: "เอกสารต่างประเทศที่มี Apostille แล้ว นำมาใช้ในไทยได้เลยไหม?",
    aShort:
      "ยังไม่ได้ทันที ระหว่างที่อนุสัญญายังไม่มีผลกับไทย เอกสารต่างประเทศต้องผ่านการรับรองจากสถานทูตไทยในประเทศนั้น แล้วนำมาแปลและทำนิติกรณ์ในไทย",
    aLong: `เส้นทางขาเข้าที่ใช้บ่อยคือ เอกสารได้รับการรับรองจากหน่วยงานต้นทาง แล้วรับรองที่สถานเอกอัครราชทูตหรือสถานกงสุลไทย จากนั้นนำเข้ามาแปลเป็นภาษาไทยและยื่นนิติกรณ์ที่กรมการกงสุลเพื่อให้หน่วยงานไทย เช่น สำนักงานเขต อำเภอ หรือศาล ยอมรับ ${FEE} ${NOTE} ${CTA}`,
    synonyms: [
      "เอกสารต่างประเทศใช้ในไทย",
      "foreign apostille use in thailand",
      "รับรองเอกสารต่างประเทศ อำเภอ",
      "แปลเอกสารต่างประเทศเป็นไทย นิติกรณ์",
    ],
    cluster: "inbound",
  },
  {
    slug: "apostille-เอกสารบริษัทต่างจากเอกสารบุคคลไหม",
    q: "เอกสารบริษัทมีขั้นตอนต่างจากเอกสารบุคคลไหม?",
    aShort:
      "ต่างที่ต้นทาง เอกสารนิติบุคคลต้องเป็นฉบับที่ออกหรือรับรองโดยกรมพัฒนาธุรกิจการค้า หรือมีผู้มีอำนาจลงนามพร้อมตราบริษัทตามหนังสือรับรอง",
    aLong: `จุดที่ทำให้เอกสารบริษัทติดขัดคือ ผู้ลงนามไม่ตรงกับผู้มีอำนาจตามหนังสือรับรองล่าสุด หรือหนังสือรับรองเก่ากว่าที่ปลายทางกำหนด ควรขอหนังสือรับรองฉบับใหม่พร้อมกันทุกครั้ง และเตรียมคำแปลของทั้งหนังสือรับรองและเอกสารหลักไปด้วย ${NOTE} ${CTA}`,
    synonyms: [
      "รับรองเอกสารบริษัท ส่งต่างประเทศ",
      "company documents legalisation thailand",
      "หนังสือรับรองบริษัท แปล รับรอง",
    ],
    cluster: "documents",
  },
  {
    slug: "apostille-ต่างจังหวัดส่งเอกสารมาได้ไหม",
    q: "อยู่ต่างจังหวัดหรือต่างประเทศ ส่งเอกสารมาดำเนินการได้ไหม?",
    aShort:
      "ได้ โดยส่งต้นฉบับพร้อมสำเนาหนังสือเดินทางหรือบัตรประชาชน และหนังสือมอบอำนาจ ระบุปลายทางและกำหนดวันที่ต้องใช้เอกสารให้ชัดเจน",
    aLong: `การส่งเอกสารทางไปรษณีย์หรือขนส่งเอกชนมีความเสี่ยงเรื่องเวลาและการสูญหาย จึงควรใช้บริการที่ติดตามสถานะได้ และถ่ายสำเนาทุกหน้าก่อนส่ง กรณีอยู่ต่างประเทศควรเผื่อเวลาขนส่งทั้งขาไปและขากลับเข้าไปในแผนด้วย ${NOTE} ${CTA}`,
    synonyms: [
      "ส่งเอกสารทำนิติกรณ์ทางไปรษณีย์",
      "legalisation service by post thailand",
      "อยู่ต่างประเทศ ทำเอกสารไทย",
    ],
    cluster: "process",
  },
  {
    slug: "apostille-เร่งด่วนทำได้เร็วสุดแค่ไหน",
    q: "แบบด่วนทำได้เร็วที่สุดแค่ไหน?",
    aShort:
      "กรมการกงสุลมีบริการทั้งแบบปกติและแบบด่วนซึ่งค่าธรรมเนียมต่างกัน แต่ระยะเวลาจริงยังขึ้นกับคิวของวันนั้น จึงควรวางแผนเป็นช่วงวันทำการ ไม่ใช่กำหนดตายตัว",
    aLong: `${FEE} นอกจากชั้นนิติกรณ์แล้ว ชั้นที่ควบคุมเวลาไม่ได้คือคิวของสถานทูตปลายทาง ซึ่งบางแห่งต้องนัดหมายล่วงหน้า การวางแผนที่ปลอดภัยคือทำงานย้อนกลับจากวันนัดยื่นจริง แล้วเผื่อเวลาสำรองสำหรับกรณีต้องแก้ไขเอกสาร ${NOTE} ${CTA}`,
    synonyms: [
      "นิติกรณ์ด่วน กี่วัน",
      "express legalisation thailand",
      "ทำเอกสารด่วน กรมการกงสุล",
    ],
    cluster: "speed",
  },
];
